import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import Modal from '../ui/Modal';
import { Banner } from '../ui/Banner';
import { useToast } from '../ui/Toast';
import {
  DangerButton, FieldHint, FieldLabel, SecondaryButton, TextInput,
} from '../../ui/common';
import { formatDate, STREFA_FIRMY } from '../../utils/format';
import { MAKS_DLUGOSC_POTWIERDZENIA, nazwaPotwierdzona } from '../../utils/rezygnacja';

// Okno „Usuń dane firmy” w panelu super-admina (decyzja Piotrka 26.10.2026): ręczne usunięcie
// danych firmy po rezygnacji, od dnia usunięcia (dzień rezygnacji + 30). Serwer
// (POST /api/super-admin/organizations/:id/delete-data, Login/backend/lib/rezygnacjaKonta.js
// #usunDanePoRezygnacji) usuwa dane tą samą drogą co automat przechowywania i sprawdza wszystkie
// warunki jeszcze raz pod blokadą wiersza firmy; okno tylko je opisuje. Potwierdzenie to wpisana
// dokładna nazwa firmy (lustro reguły serwera w utils/rezygnacja.js#nazwaPotwierdzona). Każdy kod
// odmowy ma własne zdanie, a okno zostaje wtedy otwarte. Odmowa, która mówi, że stan firmy się
// zmienił, i brak odpowiedzi odświeżają listę (`onStale`), bo wiersz pokazywałby stary stan
// z czynnym przyciskiem. Bez odpowiedzi nie wiadomo, czy usunięcie przeszło: serwer odpowiada
// dopiero po zatwierdzeniu i skasowaniu plików, więc zerwane połączenie bywa już po usunięciu.
// W trakcie wysyłki okna nie da się zamknąć, bo usunięcie już trwa, a po odmowie fokus wraca do
// okna (wyłączony na czas wysyłki przycisk traci fokus i przeglądarka przenosi go na stronę pod
// oknem). Ręczne usunięcie nie wysyła firmie żadnego maila.

function csrfToken() {
  const parts = `; ${document.cookie}`.split('; csrf=');
  return parts.length === 2 ? parts.pop().split(';').shift() : '';
}

const nicNieRob = () => {};

// Odmowy, po których wiersz na liście pokazuje stan firmy sprzed zmiany: lista jest pobierana od nowa.
const KODY_ZMIENIONEGO_STANU = new Set(['not_resigned', 'resignation_delete_not_yet', 'delete_conflict']);

/**
 * Czy nie wiadomo, jak skończyło się usunięcie: brak odpowiedzi (zerwane połączenie) albo błąd
 * serwera bez treści z naszej trasy (strona pośrednika, np. 502). Odpowiedź trasy zawsze niesie
 * `error` albo `code`, a błąd przed zatwierdzeniem kończy się wycofaniem.
 */
export function nieznanyWynik(odpowiedz, dane) {
  if (!odpowiedz) return true;
  const d = dane || {};
  return odpowiedz.status >= 500 && !d.error && !d.code;
}

const Akapit = styled.p`
  margin: 0 0 var(--sp-3);
  color: var(--text);
  line-height: 1.5;
`;

const Lista = styled.ul`
  margin: 0 0 var(--sp-3);
  padding-left: var(--sp-5);
  color: var(--text);
  line-height: 1.5;

  li + li {
    margin-top: var(--sp-1);
  }
`;

const Drobne = styled.p`
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-sm);
  color: var(--text-muted);
  line-height: 1.5;
`;

const Pole = styled.div`
  margin-top: var(--sp-4);
`;

const Nazwa = styled.strong`
  overflow-wrap: anywhere;
  user-select: all;
`;

const Odstep = styled.div`
  margin-top: var(--sp-3);
`;

/**
 * Zdanie dla odpowiedzi serwera innej niż 200. Klucze wypisane wprost, bo audyt tłumaczeń widzi
 * tylko stałe klucze.
 */
export function komunikatBledu(dane, status, t, dzienPL) {
  const d = dane || {};
  switch (d.code) {
    case 'not_resigned':
      return t('deleteResigned.error.notResigned');
    case 'resignation_delete_not_yet': {
      const od = dzienPL(d.deleteFrom);
      return od ? t('deleteResigned.error.notYet', { od }) : t('deleteResigned.error.notYetNoDate');
    }
    case 'ksef_pending':
      return t('deleteResigned.error.ksefPending', { liczba: Number(d.pendingKsefInvoices) || 0 });
    case 'confirm_name_mismatch':
      return t('deleteResigned.error.nameMismatch');
    case 'delete_platform_account':
      return t('deleteResigned.error.platformAccount');
    case 'delete_demo':
      return t('deleteResigned.error.demo');
    case 'delete_conflict':
      return t('deleteResigned.error.conflict');
    case '2fa_required':
    case '2fa_session_required':
      return t('deleteResigned.error.session');
    default:
      break;
  }
  if (status === 404) return t('deleteResigned.error.notFound');
  if (status === 401 || status === 403) return t('deleteResigned.error.session');
  return t('deleteResigned.error.generic');
}

export default function DeleteResignedDataModal({ org, onClose, onDeleted, onStale = nicNieRob }) {
  const { t, currentLanguage } = useTranslation();
  const toast = useToast();
  const inputRef = useRef(null);
  const przyciskRef = useRef(null);
  // Druga wysyłka przed odświeżeniem stanu `busy` (podwójne kliknięcie) nie może pójść.
  const wysylaRef = useRef(false);
  const [wpis, setWpis] = useState('');
  const [busy, setBusy] = useState(false);
  const [blad, setBlad] = useState('');
  // Licznik nieudanych wysyłek: po każdej fokus wraca do okna, gdy przycisk znów jest czynny.
  const [powrotFokusu, setPowrotFokusu] = useState(0);

  useEffect(() => {
    if (!powrotFokusu) return;
    const przycisk = przyciskRef.current;
    const cel = przycisk && !przycisk.disabled ? przycisk : inputRef.current;
    if (cel) cel.focus();
  }, [powrotFokusu]);

  const dzienPL = (ymd) => formatDate(ymd, { locale: currentLanguage, fallback: '' });
  // resigned_at to znacznik czasu (północ w Warszawie), dzień usunięcia to sam dzień.
  const odDnia = formatDate(org.resigned_at, { locale: currentLanguage, fallback: '', timeZone: STREFA_FIRMY });
  const usuniecieOd = dzienPL(org.resignation_delete_from);
  const potwierdzona = nazwaPotwierdzona(wpis, org.name);

  const wyslij = async () => {
    if (wysylaRef.current || !potwierdzona) return;
    wysylaRef.current = true;
    setBusy(true);
    setBlad('');
    let odpowiedz = null;
    let dane = {};
    try {
      odpowiedz = await fetch(`${API_BASE}/api/super-admin/organizations/${org.id}/delete-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        credentials: 'include',
        body: JSON.stringify({ confirmName: wpis }),
      });
      dane = await odpowiedz.json().catch(() => ({}));
    } catch (e) {
      odpowiedz = null;
    }
    if (odpowiedz && odpowiedz.ok) {
      toast.success(t('deleteResigned.done', { name: org.name }));
      onDeleted();
      return;
    }
    const nieznany = nieznanyWynik(odpowiedz, dane);
    setBlad(nieznany ? t('deleteResigned.error.unknownOutcome') : komunikatBledu(dane, odpowiedz.status, t, dzienPL));
    if (nieznany || odpowiedz.status === 404 || KODY_ZMIENIONEGO_STANU.has(dane && dane.code)) onStale();
    wysylaRef.current = false;
    setBusy(false);
    setPowrotFokusu((n) => n + 1);
  };

  return (
    <Modal
      open
      onClose={busy ? nicNieRob : onClose}
      dismissOnBackdrop={!busy}
      title={t('deleteResigned.title', { name: org.name })}
      size="md"
      initialFocus={inputRef}
      closeLabel={t('common.close')}
      data-cy="delete-data-modal"
      footer={(
        <>
          <SecondaryButton type="button" onClick={onClose} disabled={busy} data-cy="delete-data-cancel">
            {t('common.cancel')}
          </SecondaryButton>
          <DangerButton
            type="button"
            ref={przyciskRef}
            onClick={wyslij}
            disabled={!potwierdzona || busy}
            data-cy="delete-data-submit"
          >
            {busy ? t('deleteResigned.submitting') : t('deleteResigned.submit')}
          </DangerButton>
        </>
      )}
    >
      <Akapit data-cy="delete-data-intro">{t('deleteResigned.intro', { od: odDnia, usuniecie: usuniecieOd })}</Akapit>
      <Akapit>{t('deleteResigned.whatTitle')}</Akapit>
      <Lista data-cy="delete-data-what">
        <li>{t('deleteResigned.whatData')}</li>
        <li>{t('deleteResigned.whatUsers')}</li>
        <li>{t('deleteResigned.whatFiles')}</li>
      </Lista>
      <Drobne data-cy="delete-data-log">{t('deleteResigned.log')}</Drobne>
      <Drobne data-cy="delete-data-backups">{t('deleteResigned.backups')}</Drobne>
      <Banner tone="warning" data-cy="delete-data-export">{t('deleteResigned.exportReminder')}</Banner>

      <Pole>
        <FieldLabel htmlFor="delete-data-confirm-name">
          {t('deleteResigned.confirmLabel')} <Nazwa data-cy="delete-data-name">{org.name}</Nazwa>
        </FieldLabel>
        <TextInput
          id="delete-data-confirm-name"
          ref={inputRef}
          type="text"
          value={wpis}
          maxLength={MAKS_DLUGOSC_POTWIERDZENIA}
          onChange={(e) => { setWpis(e.target.value); setBlad(''); }}
          readOnly={busy}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          aria-describedby="delete-data-confirm-hint"
          data-cy="delete-data-confirm-name"
        />
        <FieldHint id="delete-data-confirm-hint">{t('deleteResigned.confirmHint')}</FieldHint>
      </Pole>

      {blad && (
        <Odstep>
          <Banner tone="danger" compact data-cy="delete-data-error">{blad}</Banner>
        </Odstep>
      )}
    </Modal>
  );
}
